// ============================================================
// THE SUPPORT RING AND THREADS (Squad Mode stage 7, ruled 2026-10-23):
// for the brother you act as, while he is in Squad Mode or waiting to
// enter, a faint ring of his kill-team's support range round his feet and
// a hairline thread to each brother on the stage inside it, with the
// metres. Drawn subtly ("there are a lot of overlays on the map"), on the
// ground, never like the gold marching threads of control or ganging up.
//
// Hidden figures are never threaded (the caller passes the shared roster).
// While Enter or Challenge is aimed at (the drum's cursor, the strip's
// button hovered), a Solo brother sees the same as a PREVIEW (2026-10-24).
//
// Pure. Zero React dependency.
// ============================================================
import type { Combatant } from '../roster/types';
import { teamOf } from '../roster/team';
import { groundDistance } from '../battlefield/measure';
import { brothersIn, isBrother, type KillTeams } from './record';

export interface SupportView {
  actorId: string;
  tag: string;
  rangeM: number;
  /** The brothers on the map within range, nearest first, with the metres to each. */
  threads: { id: string; metres: number }[];
  /** Drawn for a Solo brother aiming at Enter or Challenge: what he would have. Absent otherwise. */
  preview?: true;
}

const tokenAt = (tokens: Readonly<Record<string, { x: number; z: number }>>, id: string) =>
  tokens[id] ? { x: tokens[id].x, y: 0, z: tokens[id].z } : null;

/**
 * What to draw for this actor; null for anyone not in (or entering) Squad Mode - unless `preview`, for a brother
 * aiming at Enter or Challenge - or off the map.
 */
export function supportView(
  actorId: string | null,
  roster: readonly Combatant[],
  teams: KillTeams,
  tokens: Readonly<Record<string, { x: number; z: number }>>,
  preview = false,
): SupportView | null {
  const b = brotherOnMap(actorId, roster, teams, tokens);
  if (!b || (!b.actor.state.killTeam && !preview)) return null;
  const threads = threadsFrom(roster, b.tag, b.actor.id, b.at, tokens, b.rangeM);
  // A preview only for a Solo brother: one in (or entering) Squad Mode sees his own.
  const previewing = !b.actor.state.killTeam;
  return {
    actorId: b.actor.id,
    tag: b.tag,
    rangeM: b.rangeM,
    threads,
    ...(previewing ? { preview: true as const } : {}),
  };
}

/** The actor as a brother of a kill-team with an Oath, on the map; null for anyone else. */
function brotherOnMap(
  actorId: string | null,
  roster: readonly Combatant[],
  teams: KillTeams,
  tokens: Readonly<Record<string, { x: number; z: number }>>,
) {
  const actor = actorId ? roster.find((c) => c.id === actorId) : undefined;
  const tag = actor && teamOf(actor)?.tag;
  const kt = tag ? teams.killTeams[tag] : undefined;
  const at = actorId ? tokenAt(tokens, actorId) : null;
  if (!actor || !tag || !kt?.oath || !at || !isBrother(actor, tag)) return null;
  return { actor, tag, at, rangeM: kt.rangeM };
}

/** The other brothers on the map within range of `at`, nearest first, with the metres. */
function threadsFrom(
  roster: readonly Combatant[],
  tag: string,
  actorId: string,
  at: { x: number; y: number; z: number },
  tokens: Readonly<Record<string, { x: number; z: number }>>,
  rangeM: number,
): SupportView['threads'] {
  return brothersIn(roster, tag)
    .filter((b) => b.id !== actorId)
    .flatMap((b) => {
      const there = tokenAt(tokens, b.id);
      return there ? [{ id: b.id, metres: groundDistance(at, there) }] : [];
    })
    .filter((t) => t.metres <= rangeM)
    .sort((a, b) => a.metres - b.metres);
}
